import { Button, Spinner } from '@monksflow/monks-ui';
import { DownloadIcon, SparklesIcon, UploadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OptimizedImage } from '@/shared/ui/OptimizedImage';
import { useProfileImageSection } from '../tabs/ProfileMediaTab/components/ProfileImageSection/hooks/useProfileImageSection';
import type { ProfileImageSectionProps } from '../tabs/ProfileMediaTab/components/ProfileImageSection/ProfileImageSection.types';
import { MediaMetadataList } from './MediaMetadataList';

export function ProfileImageSectionV2({
  photoAssetId,
  isProcessing,
  processingLabel,
  disabled,
  onUploadClick,
  onRegenerateClick,
  metadata,
}: ProfileImageSectionProps) {
  const { t } = useTranslation('audiences');
  const { downloadUrl, isLoadingUrl, isHovered, setIsHovered, handleDownload } =
    useProfileImageSection(photoAssetId);

  const showLoadingSpinner = photoAssetId && isLoadingUrl;
  const showImage = downloadUrl && !isLoadingUrl;
  const showEmptyState = !photoAssetId && !isProcessing;
  const controlsDisabled = disabled ?? isProcessing;

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex items-center justify-between gap-sm">
        <span className="text-sm font-medium text-foreground">
          {t('audiences.profileImageSection.profileImage')}
        </span>
        <div className="flex items-center gap-xs">
          <Button variant="outline" size="sm" onClick={onUploadClick} disabled={controlsDisabled}>
            <UploadIcon className="size-4" />
            {t('audiences.profileImageSection.uploadFile')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onRegenerateClick}
            disabled={controlsDisabled}
          >
            <SparklesIcon className="size-4" />
            {t('audiences.profileImageSection.reGenerateImage')}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-md">
        <div
          className={`relative size-40 shrink-0 overflow-hidden rounded-md border border-border ${
            showEmptyState ? 'bg-muted' : ''
          }`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {showEmptyState && (
            <div className="flex size-full items-center justify-center">
              <span className="text-sm text-muted-foreground">
                {t('audiences.profileImageSection.noImage')}
              </span>
            </div>
          )}

          {showLoadingSpinner && (
            <div className="flex size-full items-center justify-center">
              <Spinner />
            </div>
          )}

          {showImage && (
            <OptimizedImage
              src={downloadUrl}
              alt={t('audiences.profileImageSection.profileImage')}
              priority
              showSkeleton
              containerClassName="size-full"
              className="size-full object-cover"
            />
          )}

          {isProcessing && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-xs bg-background/80">
              <Spinner />
              {processingLabel && (
                <span className="text-xs text-muted-foreground">{processingLabel}</span>
              )}
            </div>
          )}

          {showImage && isHovered && (
            <Button
              variant="ghost"
              size="mini"
              className="absolute right-2 top-2 bg-background/90 hover:bg-background"
              aria-label={t('audiences.profileImageSection.download')}
              onClick={handleDownload}
            >
              <DownloadIcon className="size-4" />
            </Button>
          )}
        </div>

        {metadata && <MediaMetadataList metadata={metadata} />}
      </div>
    </div>
  );
}

ProfileImageSectionV2.displayName = 'ProfileImageSectionV2';
